<script setup>
import AnnouncementPopup from '@/components/AnnouncementPopup.vue';
import CookieConsent from '@/components/CookieConsent.vue';
import ProductCard from '@/components/ProductCard.vue';
import { slugify } from '@/utils/slugify';
import { Head, Link, usePage } from '@inertiajs/vue3';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { route } from 'ziggy-js';

const { t, locale } = useI18n();
const { props } = usePage();

const categories = ref(props.categories || []);
const banners = ref(props.banners || []);
const site = ref(props.siteInformation || {});

const phone = computed(() => site.value?.phone_number || '');
const waLink = computed(() => 'https://wa.me/' + String(phone.value).replace(/[^0-9]/g, ''));
const homeUrl = computed(() => `/${locale.value}`);

const dashboard_title = computed(() => {
    if (site.value?.dashboard_title) return site.value.dashboard_title;
    if (site.value?.title) return `${site.value.title} - Güvenle Oyun Al`;
    return 'Güvenle Oyun Al';
});

function imageUrl(raw) {
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return '/storage/' + String(raw).replace(/^\/?storage\//, '');
}

function catLink(category) {
    return route(locale.value === 'tr' ? 'categories.show-client.tr' : 'categories.show-client.en', {
        id: category.id,
        title: slugify(category.title),
        locale: locale.value,
    });
}

// ---- Slider
const current = ref(0);
const canSlide = computed(() => (banners.value?.length || 0) > 1);
let timer = null;
function go(i) {
    if (!banners.value.length) return;
    current.value = (i + banners.value.length) % banners.value.length;
}
function next() {
    go(current.value + 1);
}
function prev() {
    go(current.value - 1);
}
function play() {
    if (timer || !canSlide.value) return;
    timer = setInterval(next, 5000);
}
function pause() {
    if (timer) clearInterval(timer);
    timer = null;
}
function restart() {
    pause();
    play();
}
function uiNext() {
    next();
    restart();
}
function uiPrev() {
    prev();
    restart();
}
function uiGo(i) {
    go(i);
    restart();
}
let startX = 0;
function onTouchStart(e) {
    startX = e.touches[0].clientX;
}
function onTouchEnd(e) {
    const dx = startX - e.changedTouches[0].clientX;
    if (dx > 50) next();
    else if (dx < -50) prev();
    restart();
}
onMounted(play);
onUnmounted(pause);
</script>

<template>
    <Head :title="dashboard_title" />

    <div class="dark min-h-screen overflow-x-clip bg-[#0b0f16] font-sans text-[#e6edf6] antialiased" style="background-image: radial-gradient(1200px 600px at 70% -10%, #16273f 0%, transparent 60%), radial-gradient(900px 500px at 0% 0%, #10203a 0%, transparent 55%)">
        <!-- Duyuru şeridi -->
        <div class="border-b border-[#1f3350] bg-gradient-to-r from-[#0a2540] via-[#0d3a5c] to-[#0a2540] px-3 py-2 text-center text-[13px] tracking-wide text-[#66c0f4]">
            🔥 Tüm oyunlar <b class="text-white">ömür boyu garantili</b> &nbsp;•&nbsp; Otomatik teslimat &nbsp;•&nbsp; 7/24 WhatsApp destek
        </div>

        <!-- HEADER -->
        <header class="sticky top-0 z-50 border-b border-[#1f3350] bg-[#0a0f17]/85 backdrop-blur-lg">
            <div class="mx-auto flex h-[64px] max-w-[1280px] items-center gap-2 px-3 sm:h-[70px] sm:gap-5 sm:px-5">
                <a :href="homeUrl" class="flex items-center gap-1.5 whitespace-nowrap text-[15px] font-black sm:gap-3 sm:text-[22px]">
                    <span class="grid h-7 w-7 place-items-center rounded-[9px] bg-gradient-to-br from-[#1a9fff] to-[#0a63c9] text-[15px] shadow-lg shadow-[#1a9fff]/40 sm:h-[38px] sm:w-[38px] sm:rounded-[11px] sm:text-[20px]">🎮</span>
                    Ucuz<span class="text-[#66c0f4]">Oyunevi</span>
                </a>
                <div class="relative hidden max-w-[560px] flex-1 md:block">
                    <span class="pointer-events-none absolute left-4 top-3 text-[18px] text-[#8ba3c7]">🔍</span>
                    <input placeholder="Oyun ara..." class="h-[44px] w-full rounded-[12px] border border-[#1f3350] bg-[#0c1826] pl-11 pr-4 text-[14px] text-[#e6edf6] outline-none transition focus:border-[#66c0f4]" />
                </div>
                <div class="ml-auto flex items-center gap-1.5 sm:gap-2.5">
                    <a :href="`${homeUrl}/login`" class="inline-flex h-9 items-center whitespace-nowrap rounded-[10px] border border-[#1f3350] bg-[#16273f] px-3 text-[12px] font-bold text-[#dbe9fb] transition hover:bg-[#1c3452] sm:h-[42px] sm:rounded-[11px] sm:px-[18px] sm:text-[14px]">Giriş Yap</a>
                    <a :href="`${homeUrl}/register`" class="hidden h-9 items-center whitespace-nowrap rounded-[10px] bg-gradient-to-br from-[#a4d007] to-[#8bc34a] px-3 text-[12px] font-bold text-[#0d1b06] shadow-lg shadow-[#a4d007]/25 transition hover:-translate-y-px sm:inline-flex sm:h-[42px] sm:rounded-[11px] sm:px-[18px] sm:text-[14px]">Kayıt Ol</a>
                </div>
            </div>
            <!-- Kategori şeridi -->
            <div class="border-t border-[#1f3350] bg-[#0c1420]/50">
                <div class="mx-auto flex max-w-[1280px] items-center gap-2 overflow-x-auto px-5 py-2">
                    <a :href="homeUrl" class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-[#66c0f4]/35 bg-[#66c0f4]/10 px-[15px] py-2 text-[13.5px] font-semibold text-[#66c0f4]">🎮 Tüm Oyunlar</a>
                    <Link v-for="c in categories" :key="'chip' + c.id" :href="catLink(c)" class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border border-transparent px-[15px] py-2 text-[13.5px] font-semibold text-[#8ba3c7] transition hover:border-[#1f3350] hover:bg-[#13233a] hover:text-white">
                        {{ c.title }}
                    </Link>
                </div>
            </div>
        </header>

        <div class="mx-auto max-w-[1280px] px-5">
            <!-- SLIDER -->
            <section
                v-if="banners.length"
                class="relative mt-6 overflow-hidden rounded-[20px] border border-[#1f3350] bg-[#0a1420] shadow-2xl"
                style="aspect-ratio: 3 / 1"
                @mouseenter="pause"
                @mouseleave="play"
                @touchstart="onTouchStart"
                @touchend="onTouchEnd"
            >
                <div class="flex h-full transition-transform duration-700 ease-out" :style="{ transform: `translateX(-${current * 100}%)` }">
                    <div v-for="(b, i) in banners" :key="b.id ?? i" class="relative h-full min-w-full">
                        <a :href="b.link || '#'" rel="noopener">
                            <img :src="imageUrl(b.image || b.image_path)" class="h-full w-full object-cover" alt="Kampanya" loading="eager" decoding="async" />
                        </a>
                    </div>
                </div>
                <button v-if="canSlide" @click="uiPrev" class="group absolute left-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-black/50 text-2xl text-white backdrop-blur transition hover:bg-[#1a9fff] sm:grid" aria-label="Önceki">‹</button>
                <button v-if="canSlide" @click="uiNext" class="group absolute right-4 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-black/50 text-2xl text-white backdrop-blur transition hover:bg-[#1a9fff] sm:grid" aria-label="Sonraki">›</button>
                <div v-if="canSlide" class="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2.5">
                    <button
                        v-for="(b, i) in banners"
                        :key="'dot' + i"
                        @click="uiGo(i)"
                        class="h-[9px] rounded-full transition-all duration-300"
                        :class="i === current ? 'w-7 bg-[#66c0f4]' : 'w-[9px] bg-white/45 hover:bg-white/70'"
                        :aria-label="`Slayt ${i + 1}`"
                    ></button>
                </div>
            </section>

            <!-- GÜVEN ŞERİDİ -->
            <div class="mt-5 grid grid-cols-2 gap-3.5 lg:grid-cols-4">
                <div v-for="(item, i) in [
                    { ic: '⚡', b: 'Otomatik Teslim', s: 'Ödeme sonrası anında' },
                    { ic: '🔒', b: 'Güvenli Ödeme', s: 'Shopier altyapısı' },
                    { ic: '💬', b: 'Hızlı Destek', s: '7/24 WhatsApp hattı' },
                    { ic: '🛡️', b: 'Ömür Boyu Garanti', s: 'İade güvencesi' },
                ]" :key="i" class="flex items-center gap-3.5 rounded-[14px] border border-[#1f3350] bg-[#111c2b] p-4">
                    <div class="grid h-[42px] w-[42px] flex-shrink-0 place-items-center rounded-[11px] bg-gradient-to-br from-[#12314f] to-[#0d2138] text-[20px] text-[#66c0f4]">{{ item.ic }}</div>
                    <div>
                        <b class="block text-[14px]">{{ item.b }}</b>
                        <small class="text-[12.5px] text-[#8ba3c7]">{{ item.s }}</small>
                    </div>
                </div>
            </div>

            <!-- KATEGORİLER / ÜRÜNLER -->
            <section class="mb-6 mt-10 space-y-12">
                <article v-for="category in categories" :key="category.id">
                    <header class="mb-5 flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <span class="h-[26px] w-[5px] rounded bg-gradient-to-b from-[#66c0f4] to-[#1a9fff]"></span>
                            <h2 class="text-xl font-extrabold tracking-tight text-white sm:text-2xl">{{ category.title }}</h2>
                        </div>
                        <Link :href="catLink(category)" class="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#66c0f4] transition hover:gap-2.5">
                            Tümünü Gör →
                        </Link>
                    </header>

                    <div v-if="category.products && category.products.length > 0" class="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5">
                        <ProductCard v-for="product in category.products" :key="product.id" :product="product" />
                    </div>
                    <div v-else class="rounded-2xl border border-dashed border-[#1f3350] bg-[#111c2b]/60 p-10 text-center text-[#8ba3c7]">
                        {{ t('app.noProduct') }}
                    </div>
                </article>
            </section>
        </div>

        <!-- FOOTER -->
        <footer class="mt-14 border-t border-[#1f3350] bg-[#0e1622] pb-8 pt-11">
            <div class="mx-auto max-w-[1280px] px-5">
                <div class="grid gap-9 sm:grid-cols-2 lg:grid-cols-4">
                    <div class="lg:col-span-1">
                        <a :href="homeUrl" class="mb-3.5 flex items-center gap-3 text-[20px] font-black">
                            <span class="grid h-[38px] w-[38px] place-items-center rounded-[11px] bg-gradient-to-br from-[#1a9fff] to-[#0a63c9] text-[20px]">🎮</span>
                            Ucuz<span class="text-[#66c0f4]">Oyunevi</span>
                        </a>
                        <p class="max-w-[320px] text-[13.5px] leading-relaxed text-[#8ba3c7]">Türkiye'nin güvenilir oyun mağazası. Steam Offline garantili hesaplar, uygun fiyat, anında teslimat.</p>
                        <a v-if="phone" :href="waLink" target="_blank" rel="noopener" class="mt-4 inline-flex items-center gap-2.5 rounded-[12px] bg-[#1b7a3d] px-[18px] py-2.5 text-[14px] font-bold text-white transition hover:bg-[#22964b]">💬 WhatsApp: {{ phone }}</a>
                    </div>
                    <div>
                        <h4 class="mb-3.5 text-[14px] text-white">Kategoriler</h4>
                        <Link v-for="c in categories" :key="'f' + c.id" :href="catLink(c)" class="mb-2.5 block text-[13.5px] text-[#8ba3c7] transition hover:text-[#66c0f4]">{{ c.title }}</Link>
                    </div>
                    <div>
                        <h4 class="mb-3.5 text-[14px] text-white">Kurumsal</h4>
                        <a href="#" class="mb-2.5 block text-[13.5px] text-[#8ba3c7] transition hover:text-[#66c0f4]">Hakkımızda</a>
                        <a href="#" class="mb-2.5 block text-[13.5px] text-[#8ba3c7] transition hover:text-[#66c0f4]">Gizlilik Politikası</a>
                        <a href="#" class="mb-2.5 block text-[13.5px] text-[#8ba3c7] transition hover:text-[#66c0f4]">Kullanım Koşulları</a>
                        <a href="#" class="mb-2.5 block text-[13.5px] text-[#8ba3c7] transition hover:text-[#66c0f4]">İletişim</a>
                    </div>
                    <div>
                        <h4 class="mb-3.5 text-[14px] text-white">Güvenli Alışveriş</h4>
                        <p class="mb-2.5 text-[13.5px] text-[#8ba3c7]">✅ Ömür boyu garanti</p>
                        <p class="mb-2.5 text-[13.5px] text-[#8ba3c7]">✅ Güvenli ödeme sistemi</p>
                        <p class="mb-2.5 text-[13.5px] text-[#8ba3c7]">✅ Otomatik teslimat</p>
                        <p class="mb-2.5 text-[13.5px] text-[#8ba3c7]">✅ 7/24 canlı destek</p>
                    </div>
                </div>
                <div class="mt-8 border-t border-[#1f3350] pt-5 text-center text-[13px] text-[#6b7f9e]">© 2026 UcuzOyunevi — Tüm hakları saklıdır.</div>
            </div>
        </footer>
    </div>

    <AnnouncementPopup v-if="props.popups" :popups="props.popups" />
    <CookieConsent storageKey="cookie_consent_v1" :expiryDays="0" policyUrl="/cerez-politikasi" />
</template>
